import i18n from "Localization";
import Notifier from "../../Notifier";
import Subscriptions from "../../Subscriptions";
import RelationshipManager from "../relationship/Manager";
import {scrubId} from "../utils";
import {
  postIncident,
  fetchIncident,
  fetchIncidentList,
  updateIncidentStatus,
  updateIncident,
  deleteIncident,
  fetchBundle,
  fetchIncidentEvents,
  fetchIncidentTactics
} from "./services";

export const subscriptionKeys = {
  linked: "ats-incident-linked",
  unlinked: "ats-incident-unlinked",
  changeStatus: "ats-incident-change-status",
  updated: "ats-incident-updated",
  created: "ats-incident-created",
  deleted: "ats-incident-deleted"
};

const sanitizeId = _incident => {
  const {id} = _incident;

  if (!id) {
    throw new Error("No incident id");
  }

  const split = id.split("/");

  if (split.length > 1) {
    _incident.id = split[split.length - 1];

    split.pop();
    _incident._path = split.join("/");
    _incident._id = id;
  }
};

export const sanitizeIncident = _incident => {
  sanitizeId(_incident);
};

class Manager {
  constructor() {
    this.subscriptionKeys = subscriptionKeys;
    this.scrubId = scrubId;
  }

  createIncident(newIncident = {}, {notify = true, notifyAction} = {}) {
    const {promise, cancel} = postIncident(newIncident);

    const _promise = promise
      .then(response => {
        if (notify) {
          Notifier.addNotification({
            title: i18n.t("incidentManager:notifications.created"),
            text: i18n.t("incidentManager:notifications.createdMessage", {
              title: newIncident.title
            }),
            theme: "info",
            action: () => {
              notifyAction && notifyAction(response);
            }
          });
        }

        Subscriptions.publish(subscriptionKeys.created, response);

        return response;
      })
      .catch(e => {
        if (notify) {
          Notifier.addNotification({
            title: i18n.t("incidentManager:notifications.error"),
            text: i18n.t("incidentManager:notifications.createErrorMessage"),
            theme: "error",
            action: notifyAction
          });
        }

        throw e;
      });

    return {promise: _promise, cancel};
  }

  getIncidentById(id) {
    const {promise, cancel} = fetchIncident(id);
    const _promise = promise.then(({data}) => data);

    return {promise: _promise, cancel};
  }

  searchIncidentById(id, options) {
    return fetchIncidentList(`id:${id}`, options);
  }

  getIncidentList(query = "", options) {
    const q = !query || query === "" ? "*" : query;

    const {promise, cancel} = fetchIncidentList(q, options);

    const _promise = promise.then(({data, headers = {}}) => {
      const {"x-total-hits": totalHits} = headers,
        total = parseInt(totalHits);

      return {
        data,
        metadata: {
          count: data.length,
          total
        }
      };
    });

    return {promise: _promise, cancel};
  }

  getIncidentBundle(incidentId, options) {
    const {promise, cancel} = fetchBundle(incidentId, options);
    const _promise = promise.then(({data}) => data);

    return {promise: _promise, cancel};
  }

  getIndicators(incidentBundle) {
    const {sightings} = incidentBundle,
      cancels = {};

    if (!sightings) {
      return;
    }

    const mappedSightingIds = sightings.map(s => s.id);

    const {promise, cancel} = fetchBundle(mappedSightingIds, {
      include_related_entities: true
    });

    cancels.privateCancel = cancel;

    const privatePromise = promise.then(({data}) => {
      const {indicators = [], relationships: sightingRelationships = []} = data;

      const indicatorIds = sightingRelationships
        .filter(r => {
          return (
            r.target_ref &&
            r.target_ref.includes("indicator-") &&
            !r.target_ref.includes("private.")
          );
        })
        .map(r => scrubId(r.target_ref));

      if (!indicatorIds.length) {
        return new Promise(resolve => {
          resolve(indicators);
        });
      }

      const {promise: publicPromise, cancel: publicCancel} = fetchBundle(
        indicatorIds,
        {
          publicURL: true,
          include_related_entities: true
        }
      );
      cancels.publicCancel = publicCancel;

      return publicPromise.then(({data: publicData}) => {
        const {indicators: publicIndicators = []} = publicData;
        return indicators.concat(publicIndicators);
      });
    });

    return {
      promise: privatePromise,
      cancel: () => {
        const {privateCancel, publicCancel} = cancels;
        privateCancel && privateCancel();
        publicCancel && publicCancel();
      }
    };
  }

  getIncidentEvents(incidentId) {
    const {promise, cancel} = fetchIncidentEvents(incidentId);

    const _promise = promise
      .then(({data}) => data)
      .catch(e => {
        // Ignore cancels
        if (e.name === "RequestCancelled") {
          return;
        }
        console.error(e);
      });

    return {promise: _promise, cancel};
  }

  getIncidentRelationshipForId(sourceId, limit = 100, offset = 0) {
    const {promise, cancel} = RelationshipManager.getRelationships(
      null,
      sourceId,
      {limit, offset}
    );

    const _promise = promise
      .then(({data}) => {
        if (!data || !data.length) {
          return {data: [], metadata: {count: 0, total: 0}};
        }

        let targets = data.map(item => `id:(*${scrubId(item.target_ref)})`);
        const incidentQuery = targets.join(" OR ");

        const {promise: listPromise} = fetchIncidentList(incidentQuery, {
          limit: data.length
        });

        return listPromise;
      })
      .then(({data, headers = {}}) => {
        const {"x-total-hits": totalHits} = headers,
          total = parseInt(totalHits);

        data.forEach(d => {
          sanitizeIncident(d);
        });

        return {
          metadata: {count: data.length, total},
          data
        };
      })
      .catch(e => {
        if (e.name === "RequestCancelled") {
          return;
        }

        console.error(e);
      });

    return {promise: _promise, cancel};
  }

  linkTargetToIncident(
    sourceId,
    incidentId,
    {notify = true, notifyAction, targetType}
  ) {
    let relationshipType =
      targetType === "snapshot_id" ? "member-of" : "related-to";
    const {promise, cancel} = RelationshipManager.createRelationship(
      incidentId,
      sourceId,
      "relationship",
      relationshipType
    );
    const _promise = promise
      .then(({data}) => {
        if (notify) {
          Notifier.addNotification({
            title: i18n.t("incidentManager:notifications.linked"),
            text: i18n.t("incidentManager:notifications.createdIncidentLink"),
            theme: "info",
            action: () => {
              notifyAction(data);
            }
          });
        }

        Subscriptions.publish(subscriptionKeys.linked, data);

        return data;
      })
      .catch(e => {
        console.error(e);

        if (notify) {
          Notifier.addNotification({
            title: i18n.t("incidentManager:notifications.linkError"),
            text: i18n.t("incidentManager:notifications.linkErrorMessage"),
            theme: "error"
          });
        }

        throw e;
      });

    return {promise: _promise, cancel};
  }

  deleteRelationship(data, targetId, sourceId, {notify}) {
    const deletePromises = data.map(r => {
      const shortId = scrubId(r.id);
      RelationshipManager.deleteRelationship(shortId);
    });
    return Promise.all(deletePromises).then(() => {
      Subscriptions.publish(subscriptionKeys.unlinked, {
        targetId,
        sourceId
      });

      if (notify) {
        Notifier.addNotification({
          title: i18n.t("incidentManager:notifications.unlinked"),
          text: i18n.t("incidentManager:notifications.removedIncidentLink"),
          theme: "info"
        });
      }
    });
  }

  unlinkTargetFromIncident(
    sourceId,
    incidentId,
    {notify = true, notifyAction, retry = false}
  ) {
    const {promise, cancel} = RelationshipManager.getRelationships(
      sourceId,
      incidentId
    );

    const _promise = promise
      .then(({data}) => {
        if (data.length === 0 && !retry) {
          // relationship is in wrong order, swap direction & try again
          return this.unlinkTargetFromIncident(incidentId, sourceId, {
            notify,
            notifyAction,
            retry: true
          });
        } else {
          return this.deleteRelationship(data, incidentId, sourceId, {
            notify,
            notifyAction
          });
        }
      })
      .catch(e => {
        console.error(e);

        if (notify) {
          Notifier.addNotification({
            title: i18n.t("incidentManager:notifications.unlinkError"),
            text: i18n.t("incidentManager:notifications.unlinkErrorMessage"),
            theme: "error"
          });
        }

        throw e;
      });

    return {promise: _promise, cancel};
  }

  changeIncidentStatus(incidentId, status, {notify = true}) {
    const {promise, cancel} = updateIncidentStatus(incidentId, status);

    const _promise = promise
      .then(({data}) => {
        if (notify) {
          Notifier.addNotification({
            title: i18n.t("incidentManager:notifications.changedStatus"),
            text: i18n.t("incidentManager:notifications.changedIncidentStatus"),
            theme: "info"
          });
        }

        Subscriptions.publish(subscriptionKeys.updated, {
          data,
          patch: {status}
        });

        return data;
      })
      .catch(e => {
        console.error(e);

        if (notify) {
          Notifier.addNotification({
            title: i18n.t("incidentManager:notifications.changeStatusError"),
            text: i18n.t(
              "incidentManager:notifications.changeStatusErrorMessage"
            ),
            theme: "error"
          });
        }

        throw e;
      });

    return {promise: _promise, cancel};
  }

  patchIncident(incidentId, patch, {notify = true}) {
    const {promise, cancel} = updateIncident(incidentId, patch);

    const _promise = promise
      .then(({data}) => {
        if (notify) {
          Notifier.addNotification({
            title: i18n.t("incidentManager:notifications.updateSuccessful"),
            text: i18n.t("incidentManager:notifications.updateSuccessMessage"),
            theme: "info"
          });
        }

        Subscriptions.publish(subscriptionKeys.updated, {data, patch});

        return data;
      })
      .catch(e => {
        console.error(e);

        if (notify) {
          if (patch.assignees) {
            Notifier.addNotification({
              title: i18n.t("incidentManager:notifications.updateError"),
              text: i18n.t(
                "incidentManager:notifications.setAssigneesErrorMessage"
              ),
              theme: "error"
            });
          } else {
            Notifier.addNotification({
              title: i18n.t("incidentManager:notifications.updateError"),
              text: i18n.t("incidentManager:notifications.updateErrorMessage"),
              theme: "error"
            });
          }
        }

        throw e;
      });

    return {promise: _promise, cancel};
  }

  deleteIncidents(ids) {
    if (!ids) {
      return;
    }

    const toDelete = Array.isArray(ids) ? ids : [ids];

    const {promise, cancel} = deleteIncident(toDelete);

    const _promise = promise.then(() => {
      Subscriptions.publish(subscriptionKeys.deleted, toDelete);
    });

    return {promise: _promise, cancel};
  }

  getIncidentTactics(incidentId) {
    const {promise, cancel} = fetchIncidentTactics(incidentId);
    const _promise = promise.then(({data}) => data);

    return {promise: _promise, cancel};
  }
}

const Configuration = () => {
  if (!window.atsSecurityRibbonIncidentManager) {
    window.atsSecurityRibbonIncidentManager = new Manager();
  }
  return window.atsSecurityRibbonIncidentManager;
};

export default Configuration();

export const ManagerClass = Manager;
